/**
 * THE record's e-mail state, in the browser — the same rule the server computes in SQL.
 *
 * The register's «E-posta» column and the detail screen's badge are one question, and the detail
 * screen answers it twice: the row it reads by code carries the server's answer, and the hydration
 * that follows recomputes one from the record's own deliveries read. That second computation used
 * `attempts.at(-1)` — the latest CLAIM delivery attempt — which is only half of the record's post,
 * so a certificate published directly had its notification delivered while its badge said
 * «Gönderilmedi». Making the server's projection whole was not enough on its own: the hydrated copy
 * is the richer one and wins on screen, so it is this function that the operator reads.
 *
 * So the rule is written once here and once in `latestDeliverySql`, and both are tested against
 * `tests/fixtures/credential-delivery-status.json` — the same arrangement the LinkedIn and date
 * rules use, for the same reason: two copies that cannot be shown to agree will stop agreeing.
 *
 *   WHICH ONE IS SHOWN  the most RECENT send, by the time it was created. Never the best one: an
 *                       old `delivered` may not hide a newer `failed`.
 *   NOT TWICE           since migration 023 a claim delivery carries BOTH links, so only the
 *                       deliveries with no attempt are candidates. The attempt represents the rest,
 *                       which it can because the claim path keeps it in step with its own delivery.
 *   NO SEND             no attempt and no delivery is `null`, which `deliveryLabel` prints as
 *                       «Gönderilmedi» — and which is not the same thing as queued.
 *
 * The words are the register's own vocabulary (`claim_delivery_status`, migration 001), because
 * that is what the column has always printed and what `deliveryLabel` translates. This is not a
 * second label table: an `email_delivery_status` is mapped into those words, here and in the SQL.
 */

/** `email_delivery_status` (migration 001) → the word the «E-posta» column prints. */
const EMAIL_STATUS_WORD: Record<string, string> = {
  queued: 'queued',
  sending: 'queued',
  /* Written ONLY while a retry is scheduled; when the attempts are spent it becomes `failed`. */
  soft_bounced: 'queued',
  sent: 'sent',
  delivered: 'delivered',
  hard_bounced: 'bounced',
  failed: 'failed',
  /* The address is blocked, so nothing was handed over at all. */
  suppressed: 'failed',
};
/**
 * The enum is closed, so an unrecognised value can only arrive with a migration this build has not
 * seen. «Under way» is the reference's own fallback and the one reading that claims nothing.
 */
const UNKNOWN_EMAIL_STATUS = 'queued';

export interface DeliveryAttemptFacts {
  status: string;
  attemptNo: number;
  createdAt: string;
}
export interface EmailDeliveryFacts {
  status: string;
  queuedAt: string;
  claimDeliveryAttemptId: string | null;
}

/** Milliseconds, or 0 for an unreadable timestamp — which then loses to every readable one. */
const at = (iso: string): number => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
};

export function latestDeliveryStatus(view: {
  attempts?: readonly DeliveryAttemptFacts[] | null;
  deliveries?: readonly EmailDeliveryFacts[] | null;
}): string | null {
  const candidates: { word: string; at: number; seq: number }[] = [];
  for (const a of view.attempts ?? [])
    candidates.push({ word: a.status, at: at(a.createdAt), seq: a.attemptNo });
  for (const d of view.deliveries ?? []) {
    if (d.claimDeliveryAttemptId !== null) continue;
    candidates.push({
      word: EMAIL_STATUS_WORD[d.status] ?? UNKNOWN_EMAIL_STATUS,
      at: at(d.queuedAt),
      seq: 0,
    });
  }
  if (candidates.length === 0) return null;
  candidates.sort((x, y) => y.at - x.at || y.seq - x.seq);
  return candidates[0]?.word ?? null;
}
